import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';
import { leavesApi, leavePoliciesApi } from '../../services/api';
import useStore from '../../store';
import './Leaves.css';

const BASE_LEAVE_TYPES = ['earned', 'sick', 'Special'];
const MENSTRUAL_MONTHLY_LIMIT = 1;

const STATUS_BADGE = {
  pending:   'badge-amber',
  approved:  'badge-emerald',
  rejected:  'badge-rose',
  cancelled: 'badge-gray',
};

const getStatusLabel = (leave) => {
  if (leave?.cancellation_requested) return 'cancel pending';
  if (leave?.status === 'pending' && leave?.reviewed_by) return 'pending super admin';
  return leave?.status;
};

const formatLeaveType = (leaveType) => {
  const normalized = String(leaveType || '').toLowerCase();
  if (normalized === 'wfh') return 'WFH';
  if (normalized === 'menstrual_leaves') return 'Menstrual Leaves';
  if (normalized === 'compensation_leave') return 'Compensation Leave';
  if (normalized === 'special') return 'Special';
  if (!leaveType) return '';
  return leaveType.charAt(0).toUpperCase() + leaveType.slice(1);
};

const getBalanceChipTitle = (leaveType) => {
  const label = formatLeaveType(leaveType);
  if (/\bleaves?\b$/i.test(label)) return label;
  return `${label} Leaves`;
};

const getBusinessDays = (startDate, endDate, isHalfDay) => {
  if (isHalfDay) return 0.5;
  const start = new Date(startDate);
  const end = new Date(endDate);
  let days = 0;
  const current = new Date(start);
  while (current <= end) {
    const dow = current.getDay();
    if (dow !== 0 && dow !== 6) days++;
    current.setDate(current.getDate() + 1);
  }
  return days;
};

const getLeaveSortValue = (leave, column) => {
  switch (column) {
    case 'employee':
      return `${leave.requester?.first_name || ''} ${leave.requester?.last_name || ''}`;
    case 'type':
      return formatLeaveType(leave.leave_type);
    case 'from':
      return new Date(leave.start_date).getTime();
    case 'to':
      return new Date(leave.end_date).getTime();
    case 'days':
      return Number(leave.days_requested || 0);
    case 'reason':
      return leave.reason || '';
    case 'status':
      return getStatusLabel(leave) || '';
    case 'reviewer':
      return `${leave.reviewer?.first_name || ''} ${leave.reviewer?.last_name || ''}`;
    default:
      return '';
  }
};

const sortLeaveRequests = (requests, sort) => [...requests].sort((left, right) => {
  const leftValue = getLeaveSortValue(left, sort.column);
  const rightValue = getLeaveSortValue(right, sort.column);
  const comparison = typeof leftValue === 'number' && typeof rightValue === 'number'
    ? leftValue - rightValue
    : String(leftValue).localeCompare(String(rightValue), undefined, { numeric: true, sensitivity: 'base' });
  return sort.direction === 'asc' ? comparison : -comparison;
});

// Buckets a raw leave_type string into a broad category — same grouping
// used by the admin "Leave Details" view (earned/vacation, sick, maternity
// or paternity all count as "maternity").
const categorizeLeaveType = (leaveType) => {
  const normalized = String(leaveType || '').toLowerCase();
  if (normalized.includes('maternity') || normalized.includes('paternity')) return 'maternity';
  if (normalized.includes('sick')) return 'sick';
  if (normalized.includes('earned') || normalized.includes('vacation')) return 'earned';
  return 'other';
};

const FIXED_CATEGORY_TABS = [
  { key: 'earned', label: 'Earned/Vacation', category: 'earned', cancelled: false },
  { key: 'sick', label: 'Sick Leave', category: 'sick', cancelled: false },
  { key: 'maternity', label: 'Maternity/Paternity', category: 'maternity', cancelled: false },
  { key: 'cancel-earned', label: 'Cancel Earned/Vacation', category: 'earned', cancelled: true },
  { key: 'cancel-sick', label: 'Cancel Sick', category: 'sick', cancelled: true },
  { key: 'cancel-maternity', label: 'Cancel Maternity/Paternity', category: 'maternity', cancelled: true },
];

// Builds "All" + the same fixed category/cancel tabs as the admin "Leave
// Details" view, plus one extra tab per other leave type present (WFH,
// Special, Birthday, etc. — types master_data doesn't track) so nothing
// becomes hidden.
const getLeaveTypeTabs = (requests) => {
  const isCancelled = (r) => r.status === 'cancelled';
  const fixedTabs = FIXED_CATEGORY_TABS.map((tab) => ({
    key: tab.key,
    label: tab.label,
    count: requests.filter((r) => categorizeLeaveType(r.leave_type) === tab.category && isCancelled(r) === tab.cancelled).length,
  }));

  const otherCounts = new Map();
  requests.forEach((r) => {
    if (categorizeLeaveType(r.leave_type) !== 'other') return;
    const key = String(r?.leave_type || '').toLowerCase();
    if (!key) return;
    otherCounts.set(key, (otherCounts.get(key) || 0) + 1);
  });
  const otherTabs = Array.from(otherCounts.keys())
    .sort((a, b) => formatLeaveType(a).localeCompare(formatLeaveType(b)))
    .map((key) => ({ key, label: formatLeaveType(key), count: otherCounts.get(key) }));

  return [{ key: 'all', label: 'All', count: requests.length }, ...fixedTabs, ...otherTabs];
};

// Matches a request against a tab key built by getLeaveTypeTabs above.
const matchesTypeTab = (request, tabKey) => {
  if (tabKey === 'all') return true;
  const fixedTab = FIXED_CATEGORY_TABS.find((t) => t.key === tabKey);
  if (fixedTab) {
    return categorizeLeaveType(request.leave_type) === fixedTab.category && (request.status === 'cancelled') === fixedTab.cancelled;
  }
  return String(request.leave_type || '').toLowerCase() === tabKey;
};

const filterBalancesForPolicy = (balances, policyData) => {
  if (!policyData?.balances?.length) return balances;
  const allowedLeaveTypes = new Set(
    policyData.balances.map((balance) => String(balance.leave_type || '').toLowerCase())
  );
  return balances.filter((balance) =>
    allowedLeaveTypes.has(String(balance.leave_type || '').toLowerCase())
  );
};

function SortableHeader({ label, column, sort, onSort }) {
  const isActive = sort.column === column;

  return (
    <th aria-sort={isActive ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="sort-header" onClick={() => onSort(column)}>
        {label}
        <span className="sort-header-indicator" aria-hidden="true">
          <span className={isActive && sort.direction === 'asc' ? 'sort-header-arrow--active' : ''}>↑</span>
          <span className={isActive && sort.direction === 'desc' ? 'sort-header-arrow--active' : ''}>↓</span>
        </span>
      </button>
    </th>
  );
}

function SkeletonBalanceChip() {
  return (
    <div className="balance-chip skeleton-balance-chip">
      <div className="skeleton-line" style={{ width: '60px', height: '10px', marginBottom: '4px' }}></div>
      <div className="skeleton-line" style={{ width: '40px', height: '24px', marginBottom: '4px' }}></div>
      <div className="skeleton-line" style={{ width: '50px', height: '10px' }}></div>
    </div>
  );
}

function SkeletonTableRow({ columnCount = 8 }) {
  return (
    <tr>
      {Array.from({ length: columnCount }).map((_, i) => (
        <td key={i}>
          <div className="skeleton-line" style={{ width: '80%', height: '16px' }}></div>
        </td>
      ))}
    </tr>
  );
}

export default function LeavesPage() {
  const { user } = useStore();
  const isAdmin = user?.role === 'admin';
  const isManager = ['admin','manager'].includes(user?.role);
  const [tab, setTab] = useState('my');

  // My requests
  const [myRequests, setMyRequests] = useState([]);
  const [balances, setBalances] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [mySort, setMySort] = useState({ column: 'from', direction: 'desc' });
  const [isLoading, setIsLoading] = useState(true);
  const [myTypeTab, setMyTypeTab] = useState('all');

  // Team requests (manager/admin)
  const [teamRequests, setTeamRequests] = useState([]);
  const [teamFilter, setTeamFilter] = useState('pending');
  const [teamSort, setTeamSort] = useState({ column: 'from', direction: 'desc' });
  const [isTeamLoading, setIsTeamLoading] = useState(true);
  const [teamTypeTab, setTeamTypeTab] = useState('all');

  // Apply modal
  const [showApply, setShowApply] = useState(false);
  const [applying, setApplying] = useState(false);
  // Guards against double-submit races: React state updates (e.g. disabling the
  // submit button) aren't applied to the DOM synchronously, so a rapid double
  // click/tap can fire handleApply twice before the button re-renders as disabled,
  // creating duplicate leave requests. This ref blocks re-entry immediately.
  const isSubmittingRef = useRef(false);
  const [form, setForm] = useState({
    leave_type: 'earned', start_date: '', end_date: '',
    half_day: false, half_day_period: 'first_half', reason: '',
  });

  // Review modal
  const [reviewModal, setReviewModal] = useState(null); // { leave, action }
  const [reviewComment, setReviewComment] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const [cancelModal, setCancelModal] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const getRequestedDays = () => {
    if (!form.start_date || !form.end_date) return 0;
    const start = new Date(form.start_date);
    const end = new Date(form.end_date);
    const daysDiff = (end - start) / (1000 * 60 * 60 * 24) + 1;
    return form.half_day ? 0.5 : Math.max(daysDiff, 0);
  };

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const year = new Date().getFullYear();
      const [myRes, balRes] = await Promise.all([
        leavesApi.list({ limit: 50 }),
        leavesApi.balance({ year }),
      ]);
      // Only show current user's requests in 'My Requests'
      setMyRequests(myRes?.data?.data?.filter((r) => r?.user_id === user?.id));
      setBalances(balRes.data.data);
      useStore.getState().setTakenSickLeave(balRes.data.takenSickLeave);
      useStore.getState().setTakenSpecialLeave(balRes.data.takenSpecialLeave);

      // Load user's assigned leave policy (if any) and show policy-based balances
      try {
        if (user?.leave_policy_id) {
          const pol = await leavePoliciesApi.get(user.leave_policy_id);
          setPolicy(pol.data.data);
        } else {
          setPolicy(null);
        }
      } catch (err) {
        // ignore policy fetch errors, fallback to global balances
        setPolicy(null);
      }
    } finally {
      setIsLoading(false);
    }

    if (isManager) {
      setIsTeamLoading(true);
      try {
        const teamRes = await leavesApi.list({ status: teamFilter === 'all' ? undefined : teamFilter, limit: 50 });
        setTeamRequests(teamRes.data.data.filter((r) => r.user_id !== user.id));
      } finally {
        setIsTeamLoading(false);
      }
    }
  }, [isManager, teamFilter, user?.id, user?.leave_policy_id]);

  useEffect(() => { load(); }, [load]);

  const handleApply = async (e) => {
    e.preventDefault();
    if (isSubmittingRef.current) return;
    if (!form.start_date || !form.end_date || !form.reason) {
      toast.error('Please fill all required fields');
      return;
    }

    if (form.leave_type.toLowerCase() === 'birthday') {
      if (!user?.date_of_birth) {
        toast.error('Please update your date of birth before requesting birthday leave');
        return;
      }
      if (form.start_date !== form.end_date) {
        toast.error('Birthday leave must be for a single day');
        return;
      }
      const dobMonthDay = String(user.date_of_birth).slice(5, 10);
      const reqMonthDay = String(form.start_date).slice(5, 10);
      if (dobMonthDay !== reqMonthDay) {
        toast.error('Birthday leave must be on your date of birth');
        return;
      }
    }

    if (form.leave_type.toLowerCase() === 'wfh') {
      const start = new Date(form.start_date);
      const end = new Date(form.end_date);
      if (start.getFullYear() !== end.getFullYear() || start.getMonth() !== end.getMonth()) {
        toast.error('WFH requests must be within a single month');
        return;
      }

      const requestedDays = getBusinessDays(form.start_date, form.end_date, form.half_day);
      const usedDays = myRequests
        .filter((r) => String(r?.leave_type || '').toLowerCase() === 'wfh')
        .filter((r) => !['rejected', 'cancelled'].includes(r.status))
        .filter((r) => {
          const d = new Date(r.start_date);
          return d.getFullYear() === start.getFullYear() && d.getMonth() === start.getMonth();
        })
        .reduce((sum, r) => sum + parseFloat(r.days_requested || 0), 0);

      if (requestedDays + usedDays > 5) {
        const remaining = Math.max(5 - usedDays, 0);
        toast.error(`WFH limit is 5 days per month. Remaining this month: ${remaining}.`);
        return;
      }
    }

    if (form.leave_type.toLowerCase() === 'menstrual_leaves') {
      const start = new Date(form.start_date);
      const end = new Date(form.end_date);
      if (start.getFullYear() !== end.getFullYear() || start.getMonth() !== end.getMonth()) {
        toast.error('Menstrual leave must be within a single month');
        return;
      }

      const requestedDays = getBusinessDays(form.start_date, form.end_date, form.half_day);
      const usedDays = myRequests
        .filter((r) => String(r?.leave_type || '').toLowerCase() === 'menstrual_leaves')
        .filter((r) => !['rejected', 'cancelled'].includes(r.status))
        .filter((r) => {
          const d = new Date(r.start_date);
          return d.getFullYear() === start.getFullYear() && d.getMonth() === start.getMonth();
        })
        .reduce((sum, r) => sum + parseFloat(r.days_requested || 0), 0);

      if (requestedDays + usedDays > MENSTRUAL_MONTHLY_LIMIT) {
        const remaining = Math.max(MENSTRUAL_MONTHLY_LIMIT - usedDays, 0);
        toast.error(`Menstrual leave is limited to ${MENSTRUAL_MONTHLY_LIMIT} day per month. Remaining this month: ${remaining}.`);
        return;
      }
    }

    // Earned leave is allowed to exceed the available balance (goes negative); no client-side block.

    isSubmittingRef.current = true;
    setApplying(true);
    try {
      await leavesApi.create(form);
      toast.success('Leave request submitted!');
      setShowApply(false);
      setForm({ leave_type: 'earned', start_date: '', end_date: '', half_day: false, half_day_period: 'first_half', reason: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit leave');
    } finally {
      isSubmittingRef.current = false;
      setApplying(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelModal) return;
    setCancelling(true);
    try {
      await leavesApi.cancel(cancelModal.id);
      toast.success('Cancellation request sent for approval');
      setCancelModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to cancel');
    } finally {
      setCancelling(false);
    }
  };

  const handleReview = async () => {
    if (!reviewModal) return;
    setReviewing(true);
    try {
      const res = await leavesApi.review(reviewModal.leave.id, {
        status: reviewModal.action,
        reviewer_comment: reviewComment,
      });
      const updatedLeave = res.data.data;
      const isStageOneApproval =
        updatedLeave.status === 'pending' &&
        updatedLeave.reviewed_by &&
        !updatedLeave.cancellation_requested;

      if (isStageOneApproval) {
        toast.success('Manager approval saved. Awaiting super admin approval.');
      } else {
        const entity = reviewModal.leave.cancellation_requested ? 'Cancellation request' : 'Leave';
        toast.success(`${entity} ${reviewModal.action}!`);
      }
      setReviewModal(null);
      setReviewComment('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Review failed');
    } finally {
      setReviewing(false);
    }
  };

  // Determine if we should display policy-based leave type restrictions
  const isPolicyMode = !!user?.leave_policy_id;

  // Keep backend balances as source of truth for currently available leave.
  const displayBalances = useMemo(
    () => filterBalancesForPolicy(balances, policy),
    [balances, policy]
  );

  const balanceMap = Object.fromEntries(
    displayBalances.map((b) => [String(b.leave_type || '').toLowerCase(), b])
  );
  const policyLeaveTypes = useMemo(
    () => (policy?.balances || []).map((b) => b.leave_type),
    [policy]
  );
  const availableLeaveTypes = useMemo(
    () => (isPolicyMode && policyLeaveTypes.length ? policyLeaveTypes : BASE_LEAVE_TYPES),
    [isPolicyMode, policyLeaveTypes]
  );
  const sortedMyRequests = useMemo(() => sortLeaveRequests(myRequests, mySort), [myRequests, mySort]);
  const sortedTeamRequests = useMemo(() => sortLeaveRequests(teamRequests, teamSort), [teamRequests, teamSort]);
  const myTypeTabs = useMemo(() => getLeaveTypeTabs(myRequests), [myRequests]);
  const teamTypeTabs = useMemo(() => getLeaveTypeTabs(teamRequests), [teamRequests]);
  const activeMyTypeTab = myTypeTabs.some((t) => t.key === myTypeTab) ? myTypeTab : 'all';
  const activeTeamTypeTab = teamTypeTabs.some((t) => t.key === teamTypeTab) ? teamTypeTab : 'all';
  const visibleMyRequests = useMemo(
    () => sortedMyRequests.filter((r) => matchesTypeTab(r, activeMyTypeTab)),
    [sortedMyRequests, activeMyTypeTab]
  );
  const visibleTeamRequests = useMemo(
    () => sortedTeamRequests.filter((r) => matchesTypeTab(r, activeTeamTypeTab)),
    [sortedTeamRequests, activeTeamTypeTab]
  );

  const toggleSort = (setSort) => (column) => {
    setSort((currentSort) => ({
      column,
      direction: currentSort.column === column && currentSort.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  useEffect(() => {
    if (availableLeaveTypes.length && !availableLeaveTypes.includes(form.leave_type)) {
      setForm((prev) => ({ ...prev, leave_type: availableLeaveTypes[0] }));
    }
  }, [availableLeaveTypes, form.leave_type]);

  return (
    <div className="leaves-page">
      <div className="page-header">
        <div>
          <div className="page-title">Leave Management</div>
          <div className="page-subtitle">
            {'Track and manage your leave requests'}
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowApply(true)}>
          + Apply for Leave
        </button>
      </div>

      {/* Balance strip */}
      <div className="leave-balance-strip animate-in">
        {isLoading ? (
          // Skeleton loaders
          Array.from({ length: 4 }).map((_, i) => (
            <SkeletonBalanceChip key={`skeleton-${i}`} />
          ))
        ) : (
          <>
            {displayBalances
              .filter((b) => !['sick', 'maternity', 'paternity', 'birthday'].includes(String(b.leave_type || '').toLowerCase()))
              .map((b) => {
              const isCompensationLeave = String(b.leave_type || '').toLowerCase() === 'compensation_leave';
              const taken = parseFloat(b.taken || 0);
              const rollover = parseFloat(b.rollover || 0);
              const available = b.balance ?? parseFloat(b.total_allocated) + rollover - taken;
              return (
                <div key={b.id} className="balance-chip">
                  <span className="balance-chip-type">{getBalanceChipTitle(b.leave_type)}</span>
                  <div className="balance-chip-details">
                    <div className="balance-chip-row">
                      <span className="balance-chip-label">Taken</span>
                      <span className="balance-chip-value">{taken}</span>
                    </div>
                    <div className="balance-chip-row">
                      <span className="balance-chip-label">Available</span>
                      <span className="balance-chip-value balance-chip-value--available">{available}</span>
                    </div>
                    {rollover > 0 && (
                      <div className="balance-chip-row">
                        <span className="balance-chip-label">Rollover</span>
                        <span className="balance-chip-value balance-chip-value--rollover">{rollover}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            <div key="sick" className="balance-chip">
              <span className="balance-chip-type">Sick Leaves</span>
              <div className="balance-chip-details">
                <div className="balance-chip-row">
                  <span className="balance-chip-label">Taken</span>
                  <span className="balance-chip-value">{useStore.getState().takenSickLeave ?? 0}</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Tabs */}
      {isManager && (
        <div className="leaves-tabs animate-in stagger-1">
          <button className={`tab-btn ${tab === 'my' ? 'tab-btn--active' : ''}`} onClick={() => setTab('my')}>My Requests</button>
          <button className={`tab-btn ${tab === 'team' ? 'tab-btn--active' : ''}`} onClick={() => setTab('team')}>
            Team Requests
            {teamRequests.filter((r) => r.status === 'pending').length > 0 && (
              <span className="tab-badge">{teamRequests.filter((r) => r.status === 'pending').length}</span>
            )}
          </button>
        </div>
      )}

      {/* My requests */}
      {tab === 'my' && (
        <div className="card animate-in stagger-2">
          {!isLoading && myRequests.length > 0 && (
            <div className="type-tabs">
              {myTypeTabs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`type-tab ${activeMyTypeTab === t.key ? 'active' : ''}`}
                  onClick={() => setMyTypeTab(t.key)}
                >
                  {t.label} <span className="type-tab-count">{t.count}</span>
                </button>
              ))}
            </div>
          )}
          {isLoading ? (
            // Skeleton loading state
            <div className="table-wrapper" style={{ border: 'none', borderRadius: 0 }}>
              <table>
                <thead><tr>
                  <SortableHeader label="Type" column="type" sort={mySort} onSort={toggleSort(setMySort)} />
                  <SortableHeader label="From" column="from" sort={mySort} onSort={toggleSort(setMySort)} />
                  <SortableHeader label="To" column="to" sort={mySort} onSort={toggleSort(setMySort)} />
                  <SortableHeader label="Days" column="days" sort={mySort} onSort={toggleSort(setMySort)} />
                  <SortableHeader label="Reason" column="reason" sort={mySort} onSort={toggleSort(setMySort)} />
                  <SortableHeader label="Status" column="status" sort={mySort} onSort={toggleSort(setMySort)} />
                  <SortableHeader label="Reviewer" column="reviewer" sort={mySort} onSort={toggleSort(setMySort)} />
                  <th></th>
                </tr></thead>
                <tbody>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <SkeletonTableRow key={`skeleton-row-${i}`} columnCount={8} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : myRequests.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">☂️</div>
              <div className="empty-state-title">No leave requests yet</div>
              <div className="empty-state-text">Click "Apply for Leave" to get started</div>
            </div>
          ) : visibleMyRequests.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">☂️</div>
              <div className="empty-state-title">No {myTypeTabs.find((t) => t.key === activeMyTypeTab)?.label} requests</div>
            </div>
          ) : (
            <div className="table-wrapper" style={{ border: 'none', borderRadius: 0 }}>
              <table>
                <thead><tr>
                  <SortableHeader label="Type" column="type" sort={mySort} onSort={toggleSort(setMySort)} />
                  <SortableHeader label="From" column="from" sort={mySort} onSort={toggleSort(setMySort)} />
                  <SortableHeader label="To" column="to" sort={mySort} onSort={toggleSort(setMySort)} />
                  <SortableHeader label="Days" column="days" sort={mySort} onSort={toggleSort(setMySort)} />
                  <SortableHeader label="Reason" column="reason" sort={mySort} onSort={toggleSort(setMySort)} />
                  <SortableHeader label="Status" column="status" sort={mySort} onSort={toggleSort(setMySort)} />
                  <SortableHeader label="Reviewer" column="reviewer" sort={mySort} onSort={toggleSort(setMySort)} />
                  <th></th>
                </tr></thead>
                <tbody>
                  {visibleMyRequests.map((lr) => (
                    <tr key={lr.id}>
                      <td><span className="leave-type-pill">{formatLeaveType(lr.leave_type)}</span></td>
                      <td>{new Date(lr.start_date).toLocaleDateString('en-IN')}</td>
                      <td>{new Date(lr.end_date).toLocaleDateString('en-IN')}</td>
                      <td><strong>{lr.days_requested}</strong></td>
                      <td style={{ maxWidth: 200 }} className="truncate" title={lr.reason}>{lr.reason}</td>
                      <td><span className={`badge ${STATUS_BADGE[lr.status]}`}>{getStatusLabel(lr)}</span></td>
                      <td style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                        {lr.reviewer ? `${lr.reviewer.first_name} ${lr.reviewer.last_name}` : '—'}
                        {lr.reviewer_comment && (
                          <div className="reviewer-comment" title={lr.reviewer_comment}>💬 {lr.reviewer_comment.slice(0,30)}…</div>
                        )}
                      </td>
                      <td>
                        {['pending','approved'].includes(lr.status) && !lr.cancellation_requested && (
                          <button className="btn btn-ghost btn-sm" style={{ color: 'var(--accent-rose)' }}
                            onClick={() => setCancelModal(lr)}>Cancel</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Team requests */}
      {tab === 'team' && isManager && (
        <div className="card animate-in stagger-2">
          <div className="card-header" style={{ paddingBottom: 12 }}>
            <h3>Team Leave Requests</h3>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {['pending','approved','rejected','all'].map((f) => (
                <button key={f} className={`btn btn-sm ${teamFilter === f ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setTeamFilter(f)}
                  disabled={isTeamLoading}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </div>
          {!isTeamLoading && teamRequests.length > 0 && (
            <div className="type-tabs">
              {teamTypeTabs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`type-tab ${activeTeamTypeTab === t.key ? 'active' : ''}`}
                  onClick={() => setTeamTypeTab(t.key)}
                >
                  {t.label} <span className="type-tab-count">{t.count}</span>
                </button>
              ))}
            </div>
          )}
          {isTeamLoading ? (
            // Skeleton loading state
            <div className="table-wrapper" style={{ border: 'none', borderRadius: 0 }}>
              <table>
                <thead><tr>
                  <SortableHeader label="Employee" column="employee" sort={teamSort} onSort={toggleSort(setTeamSort)} />
                  <SortableHeader label="Type" column="type" sort={teamSort} onSort={toggleSort(setTeamSort)} />
                  <SortableHeader label="From" column="from" sort={teamSort} onSort={toggleSort(setTeamSort)} />
                  <SortableHeader label="To" column="to" sort={teamSort} onSort={toggleSort(setTeamSort)} />
                  <SortableHeader label="Days" column="days" sort={teamSort} onSort={toggleSort(setTeamSort)} />
                  <SortableHeader label="Reason" column="reason" sort={teamSort} onSort={toggleSort(setTeamSort)} />
                  <SortableHeader label="Status" column="status" sort={teamSort} onSort={toggleSort(setTeamSort)} />
                  <th>Actions</th>
                </tr></thead>
                <tbody>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <SkeletonTableRow key={`skeleton-team-row-${i}`} columnCount={8} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : teamRequests.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">✅</div>
              <div className="empty-state-title">No requests to review</div>
            </div>
          ) : visibleTeamRequests.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">✅</div>
              <div className="empty-state-title">No {teamTypeTabs.find((t) => t.key === activeTeamTypeTab)?.label} requests</div>
            </div>
          ) : (
            <div className="table-wrapper" style={{ border: 'none', borderRadius: 0 }}>
              <table>
                <thead><tr>
                  <SortableHeader label="Employee" column="employee" sort={teamSort} onSort={toggleSort(setTeamSort)} />
                  <SortableHeader label="Type" column="type" sort={teamSort} onSort={toggleSort(setTeamSort)} />
                  <SortableHeader label="From" column="from" sort={teamSort} onSort={toggleSort(setTeamSort)} />
                  <SortableHeader label="To" column="to" sort={teamSort} onSort={toggleSort(setTeamSort)} />
                  <SortableHeader label="Days" column="days" sort={teamSort} onSort={toggleSort(setTeamSort)} />
                  <SortableHeader label="Reason" column="reason" sort={teamSort} onSort={toggleSort(setTeamSort)} />
                  <SortableHeader label="Status" column="status" sort={teamSort} onSort={toggleSort(setTeamSort)} />
                  <th>Actions</th>
                </tr></thead>
                <tbody>
                  {visibleTeamRequests.map((lr) => (
                    <tr key={lr.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div className="avatar avatar-sm">
                            {lr.requester?.first_name?.[0]}{lr.requester?.last_name?.[0]}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{lr.requester?.first_name} {lr.requester?.last_name}</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{lr.requester?.designation}</div>
                          </div>
                        </div>
                      </td>
                      <td><span className="leave-type-pill">{formatLeaveType(lr.leave_type)}</span></td>
                      <td>{new Date(lr.start_date).toLocaleDateString('en-IN')}</td>
                      <td>{new Date(lr.end_date).toLocaleDateString('en-IN')}</td>
                      <td><strong>{lr.days_requested}</strong></td>
                      <td style={{ maxWidth: 180 }} className="truncate" title={lr.reason}>{lr.reason}</td>
                      <td><span className={`badge ${STATUS_BADGE[lr.status]}`}>{getStatusLabel(lr)}</span></td>
                      <td>
                        {lr.status === 'pending' && (lr.cancellation_requested || user?.role === 'admin' || !lr.reviewed_by) && (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="btn btn-sm" style={{ background: '#D1FAE5', color: '#065F46', border: 'none' }}
                              onClick={() => { setReviewModal({ leave: lr, action: 'approved' }); setReviewComment(''); }}>
                              ✓ Approve
                            </button>
                            <button className="btn btn-sm" style={{ background: '#FEE2E2', color: '#991B1B', border: 'none' }}
                              onClick={() => { setReviewModal({ leave: lr, action: 'rejected' }); setReviewComment(''); }}>
                              ✕ Reject
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Apply modal */}
      {showApply && (
        <div className="modal-overlay" onClick={() => setShowApply(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Apply for Leave</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowApply(false)}>✕</button>
            </div>
            <form onSubmit={handleApply}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-group">
                  <label className="form-label">Leave Type *</label>
                  <select className="form-select" value={form.leave_type} onChange={(e) => setForm({ ...form, leave_type: e.target.value })}>
                    {availableLeaveTypes.map((t) => (
                      <option key={t} value={t}>
                        {formatLeaveType(t)}
                        {!['sick', 'special', 'compensation_leave', 'maternity', 'paternity', 'birthday'].includes(t.toLowerCase()) && balanceMap[String(t || '').toLowerCase()]
                          ? ` (${balanceMap[String(t || '').toLowerCase()].balance ?? '?'} available)`
                          : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-grid-2col">
                  <div className="form-group">
                    <label className="form-label">Start Date *</label>
                    <input type="date" className="form-input" value={form.start_date}
                      onChange={(e) => setForm({ ...form, start_date: e.target.value, end_date: form.leave_type.toLowerCase() === 'birthday' ? e.target.value : form.end_date })} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">End Date *</label>
                    <input type="date" className="form-input" value={form.leave_type.toLowerCase() === 'birthday' ? form.start_date : form.end_date}
                      min={form.start_date || undefined}
                      onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                      disabled={form.leave_type.toLowerCase() === 'birthday'}
                      required />
                  </div>
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    <input type="checkbox" checked={form.half_day} onChange={(e) => setForm({ ...form, half_day: e.target.checked })} />
                    Half Day
                  </label>
                </div>
                {form.half_day && (
                  <div className="form-group">
                    <label className="form-label">Half Day Period</label>
                    <select className="form-select" value={form.half_day_period} onChange={(e) => setForm({ ...form, half_day_period: e.target.value })}>
                      <option value="first_half">First Half (Morning)</option>
                      <option value="second_half">Second Half (Afternoon)</option>
                    </select>
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">{form.leave_type.toLowerCase() === 'special' ? 'Special Leave Type & Reason *' : 'Reason *'}</label>
                  <textarea className="form-textarea" placeholder={form.leave_type.toLowerCase() === 'special' ? "Specify the type of special leave and your reason..." : "Reason for leave…"} value={form.reason}
                    onChange={(e) => setForm({ ...form, reason: e.target.value })} required />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowApply(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={applying}>
                  {applying ? 'Submitting…' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Review modal */}
      {reviewModal && (
        <div className="modal-overlay" onClick={() => setReviewModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {reviewModal.action === 'approved'
                  ? reviewModal.leave.cancellation_requested ? '✅ Approve Cancellation' : '✅ Approve Leave'
                  : reviewModal.leave.cancellation_requested ? '❌ Reject Cancellation' : '❌ Reject Leave'}
              </h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setReviewModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '0.875rem', marginBottom: 12, color: 'var(--text-secondary)' }}>
                <strong>{reviewModal.leave.requester?.first_name} {reviewModal.leave.requester?.last_name}</strong> —{' '}
                {formatLeaveType(reviewModal.leave.leave_type)} leave{reviewModal.leave.cancellation_requested ? ' cancellation' : ''},{' '}
                {new Date(reviewModal.leave.start_date).toLocaleDateString('en-IN')} to{' '}
                {new Date(reviewModal.leave.end_date).toLocaleDateString('en-IN')} ({reviewModal.leave.days_requested} days)
              </p>
              <div className="form-group">
                <label className="form-label">Comment (optional)</label>
                <textarea className="form-textarea" value={reviewComment}
                  placeholder="Add a message to the employee…"
                  onChange={(e) => setReviewComment(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setReviewModal(null)}>Cancel</button>
              <button
                className={`btn ${reviewModal.action === 'approved' ? 'btn-primary' : 'btn-danger'}`}
                onClick={handleReview}
                disabled={reviewing}
              >
                {reviewing ? 'Saving…' : reviewModal.action === 'approved' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel confirmation modal */}
      {cancelModal && (
        <div className="modal-overlay" onClick={() => setCancelModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Cancel Leave Request</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setCancelModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '0.875rem', marginBottom: 12, color: 'var(--text-secondary)' }}>
                This will send a cancellation request to your manager for approval.
              </p>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                <strong>{formatLeaveType(cancelModal.leave_type)}</strong> leave from{' '}
                {new Date(cancelModal.start_date).toLocaleDateString('en-IN')} to{' '}
                {new Date(cancelModal.end_date).toLocaleDateString('en-IN')} ({cancelModal.days_requested} days)
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setCancelModal(null)} disabled={cancelling}>Keep Request</button>
              <button className="btn btn-danger" onClick={handleCancel} disabled={cancelling}>
                {cancelling ? 'Sending…' : 'Confirm Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
